import { History, Location } from 'history';
import { AnyAction, Middleware, Reducer } from 'redux';

// Action Types
export const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
export const CALL_HISTORY_METHOD = '@@router/CALL_HISTORY_METHOD';

// Router Action Types
export enum RouterActionType {
  PUSH = 'PUSH',
  REPLACE = 'REPLACE',
  GO = 'GO',
  GO_BACK = 'GO_BACK',
  GO_FORWARD = 'GO_FORWARD',
}

// Router State Type
export interface RouterState {
  location: Location;
  action: string;
}

// Helper to create router actions
interface RouterMethodPayload {
  args: [string, any?] | [number] | []; // Explicitly define possible tuple types
  method: string;
}

// Router action creator for history methods
export const push = (path: string, state?: any) => ({
  type: CALL_HISTORY_METHOD,
  payload: {
    method: RouterActionType.PUSH,
    args: [path, state],
  },
});

export const replace = (path: string, state?: any) => ({
  type: CALL_HISTORY_METHOD,
  payload: {
    method: RouterActionType.REPLACE,
    args: [path, state],
  },
});

export const go = (index: number) => ({
  type: CALL_HISTORY_METHOD,
  payload: {
    method: RouterActionType.GO,
    args: [index],
  },
});

export const goBack = () => ({
  type: CALL_HISTORY_METHOD,
  payload: {
    method: RouterActionType.GO_BACK,
    args: [],
  },
});

export const goForward = () => ({
  type: CALL_HISTORY_METHOD,
  payload: {
    method: RouterActionType.GO_FORWARD,
    args: [],
  },
});

// Router Middleware
export const createRouterMiddleware = (history: History): Middleware => 
  store => next => (action: AnyAction & { payload?: RouterMethodPayload }) => {
    if (action.type !== CALL_HISTORY_METHOD) {
      return next(action);
    }

    const { method, args } = action.payload;

    switch (method) {
      case RouterActionType.PUSH:
        history.push(...(args as [string, any?]));
        break;
      case RouterActionType.REPLACE:
        history.replace(...(args as [string, any?]));
        break;
      case RouterActionType.GO:
        history.go(...(args as [number]));
        break;
      case RouterActionType.GO_BACK:
        history.goBack();
        break;
      case RouterActionType.GO_FORWARD:
        history.goForward();
        break;
      default:
        break;
    }

    return next(action);
  };

// Create Router Reducer
export const createRouterReducer = (history: History): Reducer<RouterState> => {
  const initialState: RouterState = {
    location: history.location,
    action: history.action,
  };

  return (state = initialState, action) => {
    if (action.type === LOCATION_CHANGE) {
      return {
        ...state,
        location: action.payload.location,
        action: action.payload.action,
      };
    }

    return state;
  };
};

// Listen for location changes
export const startListening = (history: History, store: any) => {
  const handleLocationChange = (location: Location, action: string) => {
    store.dispatch({
      type: LOCATION_CHANGE,
      payload: {
        location,
        action,
        isFirstRendering: false,
      },
    });
  };

  // Listen for location changes and dispatch them to the store
  const unlisten = history.listen(handleLocationChange);

  // Dispatch initial location
  store.dispatch({
    type: LOCATION_CHANGE,
    payload: {
      location: history.location,
      action: history.action,
      isFirstRendering: true,
    },
  });

  return unlisten;
};